import { api } from './api';

// Review status type
export type ReviewStatus = 'none' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'revision_requested';

// Review action type
export type ReviewAction = 'approve' | 'reject' | 'revision';

// Reviewer info
export interface Reviewer {
  id: string;
  email: string;
}

// Review queue item (asset with review info)
export interface ReviewQueueItem {
  id: string;
  organization_id: string;
  original_url: string;
  processed_url: string | null;
  original_filename: string;
  status: string;
  review_status: ReviewStatus;
  reviewer_id: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  revision_count: number;
  applied_transformations: Array<{ type: string; [key: string]: any }> | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  reviewer?: Reviewer | null;
}

// Review queue response
export interface ReviewQueueResponse {
  items: ReviewQueueItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Review counts by status
export interface ReviewCounts {
  none: number;
  pending: number;
  in_review: number;
  approved: number;
  rejected: number;
  revision_requested: number;
  total: number;
}

// Submit for review result
export interface SubmitForReviewResult {
  success: boolean;
  submitted: number;
  failed: number;
  errors?: Array<{ id: string; reason: string }>;
}

// Batch review result
export interface BatchReviewResult {
  action: ReviewAction;
  total: number;
  succeeded: number;
  failed: number;
  failures?: Array<{ id: string; error: string }>;
}

// Review history entry
export interface ReviewHistoryEntry {
  id: string;
  image_id: string;
  reviewer_id: string;
  action: string;
  notes: string | null;
  created_at: string;
  reviewer?: Reviewer | null;
}

// Queue filter options
export interface ReviewQueueFilters {
  status?: ReviewStatus | ReviewStatus[];
  transformationType?: string;
  dateFrom?: string;
  dateTo?: string;
  reviewerId?: string;
  page?: number;
  limit?: number;
}

class ReviewService {
  private endpoint = '/review';

  /**
   * Get review queue with filters
   */
  async getQueue(filters: ReviewQueueFilters = {}): Promise<ReviewQueueResponse> {
    const params = new URLSearchParams();

    if (filters.status) {
      if (Array.isArray(filters.status)) {
        params.set('status', filters.status.join(','));
      } else {
        params.set('status', filters.status);
      }
    }
    if (filters.transformationType) params.set('transformationType', filters.transformationType);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.reviewerId) params.set('reviewerId', filters.reviewerId);
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = `${this.endpoint}/queue${queryString ? `?${queryString}` : ''}`;

    return api.get<ReviewQueueResponse>(url);
  }

  /**
   * Get review counts by status
   */
  async getCounts(): Promise<ReviewCounts> {
    return api.get<ReviewCounts>(`${this.endpoint}/counts`);
  }

  /**
   * Submit assets for review
   */
  async submitForReview(imageIds: string[]): Promise<SubmitForReviewResult> {
    return api.post<SubmitForReviewResult>(`${this.endpoint}/submit`, { imageIds });
  }

  /**
   * Approve an asset
   */
  async approve(imageId: string, notes?: string): Promise<ReviewQueueItem> {
    return api.post<ReviewQueueItem>(
      `${this.endpoint}/${imageId}/approve`,
      { notes }
    );
  }

  /**
   * Reject an asset
   */
  async reject(imageId: string, notes?: string): Promise<ReviewQueueItem> {
    return api.post<ReviewQueueItem>(
      `${this.endpoint}/${imageId}/reject`,
      { notes }
    );
  }

  /**
   * Request revision for an asset
   */
  async requestRevision(imageId: string, notes: string): Promise<ReviewQueueItem> {
    return api.post<ReviewQueueItem>(
      `${this.endpoint}/${imageId}/revision`,
      { notes }
    );
  }

  /**
   * Batch review action
   */
  async batchReview(imageIds: string[], action: ReviewAction, notes?: string): Promise<BatchReviewResult> {
    return api.post<BatchReviewResult>(
      `${this.endpoint}/batch`,
      { imageIds, action, notes }
    );
  }

  /**
   * Get review history for an asset
   */
  async getHistory(imageId: string): Promise<ReviewHistoryEntry[]> {
    return api.get<ReviewHistoryEntry[]>(`${this.endpoint}/${imageId}/history`);
  }

  /**
   * Get approved assets for export
   */
  async getApprovedAssets(options: { dateFrom?: string; dateTo?: string; limit?: number } = {}): Promise<{
    assets: ReviewQueueItem[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (options.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options.dateTo) params.set('dateTo', options.dateTo);
    if (options.limit) params.set('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `${this.endpoint}/approved${queryString ? `?${queryString}` : ''}`;

    return api.get<{ assets: ReviewQueueItem[]; total: number }>(url);
  }

  /**
   * Get approved assets pending download (not yet downloaded)
   */
  async getApprovedPendingDownload(options: { limit?: number } = {}): Promise<{
    assets: ApprovedAssetForDownload[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', options.limit.toString());

    const queryString = params.toString();
    const url = `${this.endpoint}/approved/pending-download${queryString ? `?${queryString}` : ''}`;

    return api.get<{ assets: ApprovedAssetForDownload[]; total: number }>(url);
  }

  /**
   * Mark assets as downloaded
   */
  async markAsDownloaded(imageIds: string[]): Promise<MarkDownloadedResult> {
    return api.post<MarkDownloadedResult>(`${this.endpoint}/mark-downloaded`, { imageIds });
  }

  /**
   * Get download statistics
   */
  async getDownloadStats(): Promise<DownloadStats> {
    return api.get<DownloadStats>(`${this.endpoint}/download-stats`);
  }

  /**
   * Bulk download approved images as a ZIP file
   * Backend automatically marks images as downloaded after successful ZIP generation
   */
  async bulkDownloadApproved(
    onProgress?: (status: string) => void
  ): Promise<BulkDownloadResult> {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('access_token');

    try {
      onProgress?.('Generating ZIP file (this may take a while)...');

      // Request ZIP from backend
      const response = await fetch(`${API_BASE_URL}/api${this.endpoint}/bulk-download-zip`, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        let message = `Failed to download: ${response.statusText}`;
        try {
          const errorData = await response.json();
          message = errorData.error?.message || errorData.message || message;
        } catch { /* response not JSON, use default message */ }
        throw new Error(message);
      }

      // Get count from header
      const imageCount = parseInt(response.headers.get('X-Image-Count') || '0', 10);

      onProgress?.('Downloading ZIP file...');

      // Get the blob
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'approved-images.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      // Images are marked as downloaded server-side after ZIP generation
      onProgress?.('Download complete!');

      return {
        success: true,
        downloaded: imageCount,
        failed: 0,
        total: imageCount
      };
    } catch (error) {
      console.error('Bulk download failed:', error);
      throw error;
    }
  }

  /**
   * Bulk download ALL approved images as a ZIP file (including already downloaded)
   */
  async bulkDownloadAllApproved(
    onProgress?: (status: string) => void
  ): Promise<BulkDownloadResult> {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('access_token');

    try {
      onProgress?.('Generating ZIP file (this may take a while)...');

      const response = await fetch(`${API_BASE_URL}/api${this.endpoint}/bulk-download-all-approved-zip`, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        let message = `Failed to download: ${response.statusText}`;
        try {
          const errorData = await response.json();
          message = errorData.error?.message || errorData.message || message;
        } catch { /* response not JSON, use default message */ }
        throw new Error(message);
      }

      const imageCount = parseInt(response.headers.get('X-Image-Count') || '0', 10);

      onProgress?.('Downloading ZIP file...');

      const blob = await response.blob();

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'all-approved-images.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      onProgress?.('Download complete!');

      return {
        success: true,
        downloaded: imageCount,
        failed: 0,
        total: imageCount
      };
    } catch (error) {
      console.error('Bulk download all approved failed:', error);
      throw error;
    }
  }

  /**
   * Bulk download ALL processed images as a ZIP file (regardless of approval status)
   */
  async bulkDownloadAll(
    onProgress?: (status: string) => void
  ): Promise<BulkDownloadResult> {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('access_token');

    try {
      onProgress?.('Generating ZIP file (this may take a while)...');

      const response = await fetch(`${API_BASE_URL}/api${this.endpoint}/bulk-download-all-zip`, {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        let message = `Failed to download: ${response.statusText}`;
        try {
          const errorData = await response.json();
          message = errorData.error?.message || errorData.message || message;
        } catch { /* response not JSON, use default message */ }
        throw new Error(message);
      }

      const imageCount = parseInt(response.headers.get('X-Image-Count') || '0', 10);

      onProgress?.('Downloading ZIP file...');

      const blob = await response.blob();

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'all-processed-images.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      onProgress?.('Download complete!');

      return {
        success: true,
        downloaded: imageCount,
        failed: 0,
        total: imageCount
      };
    } catch (error) {
      console.error('Bulk download all failed:', error);
      throw error;
    }
  }
}

// Asset for download (lighter version)
export interface ApprovedAssetForDownload {
  id: string;
  original_filename: string;
  original_url: string;
  processed_url: string | null;
  status: string;
  review_status: ReviewStatus;
  reviewed_at: string | null;
  width: number | null;
  height: number | null;
  file_size_bytes: number | null;
  format: string | null;
}

// Mark as downloaded result
export interface MarkDownloadedResult {
  success: boolean;
  marked: number;
  requested: number;
}

// Download statistics
export interface DownloadStats {
  totalApproved: number;
  pendingDownload: number;
  alreadyDownloaded: number;
  totalProcessed: number;
}

// Bulk download result
export interface BulkDownloadResult {
  success: boolean;
  downloaded: number;
  failed: number;
  total: number;
}

export const reviewService = new ReviewService();
